import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { usePlatformAuth } from "@/contexts/PlatformAuthContext";

// Shared translate state, reused across every surface (Watch, music, community,
// shop, press, ...). `fetchTranslation(lang)` returns an object of translated
// fields (or throws). Falls back to originals on any failure.
//
// options.auto = subscriber-platform "no-tap" mode: when the viewer's language
// is not English, fetch + show the translation immediately (no tap) and the
// control reads "See original". Public surfaces omit `auto` and stay tap-only.
export function useContentTranslation(originalFields, fetchTranslation, options = {}) {
  const { auto: autoOpt = false, key = null } = options;
  const { i18n } = useTranslation();
  const _pa = usePlatformAuth() || {};
  const isSubscriber = !!_pa.isSubscriber;
  // Translation is a member benefit: never auto-translate for a non-subscriber,
  // so the paywall (opened from TranslateToggle) is not bypassed by the no-tap
  // auto fetch. They still see the control; tapping it upsells.
  const auto = autoOpt && isSubscriber;
  const lang = (i18n.language || "en").split("-")[0];
  const [translated, setTranslated] = useState(null);
  const [showOriginal, setShowOriginal] = useState(false);
  const [translating, setTranslating] = useState(false);
  const [slow, setSlow] = useState(false);
  const autoFetchedFor = useRef(null);
  const softTimer = useRef(null);

  // Reviewed-translation wait budget. The backend serves the reviewed text and
  // hard-falls back to the Azure text at ~8s, so a response always arrives by
  // then. SOFT (6s) flips a "still working" hint; HARD (10s = 8s server + a
  // network buffer) is a client safety net so no one is ever stuck on a spinner.
  const SOFT_MS = 6000;
  const HARD_MS = 10000;

  const clearSoft = () => { if (softTimer.current) { clearTimeout(softTimer.current); softTimer.current = null; } };

  // Reset when the language OR the source entity (options.key) changes, so a
  // singleton surface like the audio player re-translates when the track swaps.
  useEffect(() => { setTranslated(null); setShowOriginal(false); setSlow(false); autoFetchedFor.current = null; clearSoft(); }, [lang, key]);
  useEffect(() => () => clearSoft(), []);

  // Runs a translation fetch behind the working-state: spinner immediately, a
  // "still working" hint after 6s, and a 10s hard cap that resolves to the
  // originals rather than hanging. Returns the translated fields or null.
  const runFetch = useCallback(async () => {
    setTranslating(true); setSlow(false);
    clearSoft();
    softTimer.current = setTimeout(() => setSlow(true), SOFT_MS);
    try {
      const data = await Promise.race([
        Promise.resolve(fetchTranslation(lang)),
        new Promise((_, reject) => setTimeout(() => reject(new Error("translate-timeout")), HARD_MS)),
      ]);
      return data || null;
    } finally {
      clearSoft();
      setTranslating(false); setSlow(false);
    }
  }, [fetchTranslation, lang]);

  // Auto no-tap fetch (platform only). Guarded by a ref keyed on lang+entity so an
  // inline fetchTranslation identity does not loop; runs once per language/entity.
  useEffect(() => {
    const token = `${lang}:${key}`;
    if (!auto || lang === "en" || autoFetchedFor.current === token) return;
    autoFetchedFor.current = token;
    let cancelled = false;
    runFetch()
      .then((data) => { if (!cancelled && data) { setTranslated(data); setShowOriginal(false); } })
      .catch(() => { if (!cancelled) autoFetchedFor.current = null; });
    return () => { cancelled = true; };
  }, [auto, lang, key]); // eslint-disable-line react-hooks/exhaustive-deps

  const showingTranslation = !!translated && !showOriginal;
  const values = {};
  for (const k of Object.keys(originalFields || {})) {
    const t = translated ? translated[k] : null;
    values[k] = showingTranslation && t != null && t !== "" ? t : originalFields[k];
  }

  const onToggle = useCallback(async (e) => {
    if (e && e.stopPropagation) e.stopPropagation();
    if (translated) { setShowOriginal((v) => !v); return; }
    try {
      const data = await runFetch();
      if (data) { setTranslated(data); setShowOriginal(false); }
    } catch { /* keep originals on failure */ }
  }, [translated, runFetch]);

  return { lang, values, translating, slow, hasTranslation: !!translated, showOriginal, showingTranslation, onToggle, auto };
}

// Lightweight auto-translated title for list cards (playlists, etc). Fetches the
// translated title for the viewer's language and returns it, falling back to the
// original. `ownerType === "subscriber"` (user's own wording) is never translated.
export function useAutoTitle(kind, id, title, ownerType) {
  const { i18n } = useTranslation();
  const lang = (i18n.language || "en").split("-")[0];
  const [display, setDisplay] = useState(title);
  useEffect(() => { setDisplay(title); }, [title]);
  useEffect(() => {
    if (lang === "en" || !id || ownerType === "subscriber") return;
    let cancelled = false;
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/music/${kind}/${id}/translate`, { params: { lang } })
      .then((r) => { if (!cancelled && r.data && r.data.title) setDisplay(r.data.title); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [kind, id, lang, ownerType]);
  return display;
}
